import { ref, computed, reactive } from 'vue';
import type { Issue } from '@/composables/services/useIssuesAPI';
import type { AvailableComm } from '@/modules/comms/types';

// Interface for persisting comm window data
export interface PersistedCommWindow {
  issueId: string;
  commId?: string;
  isMinimized: boolean;
  isFullscreen: boolean;
  position: { x: number; y: number };
  size: { width: number; height: number };
}

// LocalStorage key for persisted comm windows
const COMM_WINDOWS_STORAGE_KEY = 'cxme-active-comm-windows';

export interface CommWindow {
  id: string;
  case: Issue;
  comm?: AvailableComm;
  isMinimized: boolean;
  isFullscreen: boolean;
  position: { x: number; y: number };
  size: { width: number; height: number };
  unreadCount: number;
  lastActivity: Date;
  zIndex: number;
}

export interface CommWindowState {
  windows: Map<string, CommWindow>;
  activeWindowId: string | null;
  nextZIndex: number;
}

const state = reactive<CommWindowState>({
  windows: new Map(),
  activeWindowId: null,
  nextZIndex: 500
});

// LocalStorage utility functions
const saveCommWindowsToStorage = (): void => {
  try {
    const persistedWindows: PersistedCommWindow[] = Array.from(state.windows.values()).map(window => ({
      issueId: window.case.id,
      commId: window.comm?.id,
      isMinimized: window.isMinimized,
      isFullscreen: window.isFullscreen,
      position: window.position,
      size: window.size
    }));
    
    localStorage.setItem(COMM_WINDOWS_STORAGE_KEY, JSON.stringify(persistedWindows));
  } catch (error) {
    console.error('💾 CommWindows: Failed to save to localStorage:', error);
  }
};

const loadCommWindowsFromStorage = (): PersistedCommWindow[] => {
  try {
    const stored = localStorage.getItem(COMM_WINDOWS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as PersistedCommWindow[];
      return parsed;
    }
  } catch (error) {
    console.error('💾 CommWindows: Failed to load from localStorage:', error);
  }
  return [];
};

const clearCommWindowsStorage = (): void => {
  try {
    localStorage.removeItem(COMM_WINDOWS_STORAGE_KEY);
  } catch (error) {
    console.error('💾 CommWindows: Failed to clear localStorage:', error);
  }
};

export function useCommWindows() {
  // Computed values
  const windows = computed(() => Array.from(state.windows.values()));
  const minimizedWindows = computed(() => windows.value.filter(w => w.isMinimized));
  const visibleWindows = computed(() => windows.value.filter(w => !w.isMinimized));
  const activeWindow = computed(() => 
    state.activeWindowId ? state.windows.get(state.activeWindowId) : null
  );

  // Generate unique window ID
  const generateWindowId = (caseId: string, commId?: string): string => {
    const timestamp = Date.now();
    return commId 
      ? `window-${caseId}-${commId}-${timestamp}`
      : `window-${caseId}-${timestamp}`;
  };

  // Calculate default size (used for fullscreen mode only)
  const getDefaultSize = (): { width: number; height: number } => {
    return {
      width: Math.floor(window.innerWidth * 0.25),
      height: 500
    };
  };

  // Open a new communication window
  const openCommWindow = (
    caseData: Issue, 
    comm?: AvailableComm,
    options?: Partial<Pick<CommWindow, 'position' | 'size' | 'isMinimized' | 'isFullscreen'>>
  ): string => {
    const windowId = generateWindowId(caseData.id, comm?.id);
    
    // Check if window already exists for this case/comm combination
    const existingWindow = windows.value.find(w => 
      w.case.id === caseData.id && 
      (comm ? w.comm?.id === comm.id : !w.comm)
    );
    
    if (existingWindow) {
      // Focus existing window instead of creating new one
      focusWindow(existingWindow.id);
      if (existingWindow.isMinimized) {
        restoreWindow(existingWindow.id);
      }
      return existingWindow.id;
    }

    const newWindow: CommWindow = {
      id: windowId,
      case: caseData,
      comm,
      isMinimized: options?.isMinimized ?? false,
      isFullscreen: options?.isFullscreen ?? false,
      position: options?.position ?? { x: 0, y: 0 }, // Will be calculated after window is created
      size: options?.size ?? { width: 375, height: 500 }, // Fixed width to match taskbar items
      unreadCount: 0,
      lastActivity: new Date(),
      zIndex: state.nextZIndex++
    };

    state.windows.set(windowId, newWindow);
    state.activeWindowId = windowId;

    if (!options?.size) {
      newWindow.size = { width: 375, height: 500 };
    }
    
    // Save to localStorage
    saveCommWindowsToStorage();
    
    return windowId;
  };

  // Close a communication window
  const closeWindow = (windowId: string): void => {
    const window = state.windows.get(windowId);
    if (!window) return;

    state.windows.delete(windowId);
    
    // If this was the active window, find next active window
    if (state.activeWindowId === windowId) {
      const remainingWindows = Array.from(state.windows.values());
      state.activeWindowId = remainingWindows.length > 0 
        ? remainingWindows[remainingWindows.length - 1].id 
        : null;
    }

    // Save to localStorage
    saveCommWindowsToStorage();
  };

  // Minimize a window
  const minimizeWindow = (windowId: string): void => {
    const window = state.windows.get(windowId);
    if (!window) return;

    window.isMinimized = true;
    window.isFullscreen = false;
    
    // If this was the active window, find next visible window
    if (state.activeWindowId === windowId) {
      const visibleWindow = visibleWindows.value.find(w => w.id !== windowId);
      state.activeWindowId = visibleWindow?.id ?? null;
    }

    // Save to localStorage
    saveCommWindowsToStorage();
  };

  // Restore a minimized window
  const restoreWindow = (windowId: string): void => {
    const window = state.windows.get(windowId);
    if (!window) return;

    window.isMinimized = false;
    window.zIndex = state.nextZIndex++;
    state.activeWindowId = windowId;

    // Save to localStorage
    saveCommWindowsToStorage();
  };

  // Calculate default position
  const getDefaultPosition = (): { x: number; y: number } => {
    return {
      x: 100,
      y: 100
    };
  };

  // Maximize/restore window size
  const toggleMaximize = (windowId: string): void => {
    const commWindow = state.windows.get(windowId);
    if (!commWindow) return;

    if (commWindow.isFullscreen) {
      commWindow.isFullscreen = false;
      // Restore to previous size/position
    } else {
      commWindow.isFullscreen = false; // First ensure not fullscreen
      // Toggle between normal and maximized
      if (commWindow.size.width < globalThis.window.innerWidth * 0.8) {
        commWindow.size = { width: Math.floor(globalThis.window.innerWidth * 0.8), height: Math.floor(globalThis.window.innerHeight * 0.8) };
        commWindow.position = { x: Math.floor(globalThis.window.innerWidth * 0.1), y: Math.floor(globalThis.window.innerHeight * 0.1) };
      } else {
        commWindow.size = getDefaultSize();
        commWindow.position = getDefaultPosition();
      }
    }

    commWindow.zIndex = state.nextZIndex++;
    state.activeWindowId = windowId;
  };

  // Toggle fullscreen mode
  const toggleFullscreen = (windowId: string): void => {
    const window = state.windows.get(windowId);
    if (!window) return;

    window.isFullscreen = !window.isFullscreen;
    window.isMinimized = false;
    window.zIndex = state.nextZIndex++;
    state.activeWindowId = windowId;
  };

  // Focus a window (bring to front)
  const focusWindow = (windowId: string): void => {
    const window = state.windows.get(windowId);
    if (!window) return;

    window.zIndex = state.nextZIndex++;
    state.activeWindowId = windowId;
  };

  // Update window position
  const updateWindowPosition = (windowId: string, position: { x: number; y: number }): void => {
    const window = state.windows.get(windowId);
    if (!window) return;

    window.position = position;
    window.lastActivity = new Date();
  };

  // Update window size
  const updateWindowSize = (windowId: string, size: { width: number; height: number }): void => {
    const window = state.windows.get(windowId);
    if (!window) return;

    window.size = size;
    window.lastActivity = new Date();
  };

  // Update unread count for a window
  const updateUnreadCount = (windowId: string, count: number): void => {
    const window = state.windows.get(windowId);
    if (!window) {
      console.warn('🔔 CommWindows: Cannot update unread count - window not found:', windowId);
      return;
    }

    window.unreadCount = count;
    window.lastActivity = new Date();
  };

  // Update last activity time for a window (for new messages)
  const updateLastActivity = (windowId: string): void => {
    const window = state.windows.get(windowId);
    if (!window) return;

    window.lastActivity = new Date();
  };

  // Get window by ID
  const getWindow = (windowId: string): CommWindow | undefined => {
    return state.windows.get(windowId);
  };

  // Find window by case and comm
  const findWindow = (caseId: string, commId?: string): CommWindow | undefined => {
    return windows.value.find(w => 
      w.case.id === caseId && 
      (commId ? w.comm?.id === commId : !w.comm)
    );
  };

  // Find window by communication ID only
  const findWindowByCommId = (commId: string): CommWindow | undefined => {
    return windows.value.find(w => w.comm?.id === commId);
  };

  // Close all windows
  const closeAllWindows = (): void => {
    state.windows.clear();
    state.activeWindowId = null;
  };

  // Close all windows for a specific case
  const closeWindowsForCase = (caseId: string): void => {
    const windowsToClose = windows.value.filter(w => w.case.id === caseId);
    windowsToClose.forEach(w => closeWindow(w.id));
  };

  // Get total unread count across all windows
  const totalUnreadCount = computed(() => 
    windows.value.reduce((total, window) => total + window.unreadCount, 0)
  );

  // Restore comm windows from localStorage
  const restoreCommWindowsFromStorage = async (
    fetchIssue: (issueId: string) => Promise<Issue | null>,
    fetchComm?: (commId: string) => Promise<AvailableComm | null>
  ): Promise<void> => {
    const persistedWindows = loadCommWindowsFromStorage();
    
    for (const persistedWindow of persistedWindows) {
      try {
        // Fetch the issue data
        const issue = await fetchIssue(persistedWindow.issueId);
        if (!issue) {
          console.warn('💾 CommWindows: Issue not found for ID:', persistedWindow.issueId);
          continue;
        }

        // Fetch the comm data if commId exists
        let comm: AvailableComm | undefined = undefined;
        if (persistedWindow.commId && fetchComm) {
          comm = await fetchComm(persistedWindow.commId) || undefined;
          if (!comm) {
            console.warn('💾 CommWindows: Comm not found for ID:', persistedWindow.commId);
            // Continue without comm - this will open the case window
          }
        }

        // Open the window with the persisted state
        openCommWindow(issue, comm, {
          isMinimized: persistedWindow.isMinimized,
          isFullscreen: persistedWindow.isFullscreen,
          position: persistedWindow.position,
          size: persistedWindow.size
        });
      } catch (error) {
        console.error('💾 CommWindows: Failed to restore window:', persistedWindow, error);
      }
    }
  };

  return {
    // State
    windows,
    minimizedWindows,
    visibleWindows,
    activeWindow,
    totalUnreadCount,

    // Actions
    openCommWindow,
    closeWindow,
    minimizeWindow,
    restoreWindow,
    toggleMaximize,
    toggleFullscreen,
    focusWindow,
    updateWindowPosition,
    updateWindowSize,
    updateUnreadCount,
    updateLastActivity,
    getWindow,
    findWindow,
    findWindowByCommId,
    closeAllWindows,
    closeWindowsForCase,

    // Persistence
    restoreCommWindowsFromStorage,
    clearCommWindowsStorage
  };
}

// Global instance for sharing across components
let globalCommWindows: ReturnType<typeof useCommWindows> | null = null;

export function useGlobalCommWindows() {
  if (!globalCommWindows) {
    globalCommWindows = useCommWindows();
  }
  return globalCommWindows;
}
